import React from "react";
import { fileUrl } from "@/lib/api";

// Compact overlapping circular avatar group for a treatment card.
// - Filters `allCrew` down to the therapists actually assigned to this treatment
//   (via `crew_ids`) — falls back to "all active crew" if the assignment list
//   is empty, matching backend behaviour.
// - Skips inactive crew, dedupes by id, keeps overlap subtle so every face
//   stays readable.
// - Native `title` attribute drives the hover / focus tooltip AND acts as an
//   accessible label for screen readers. Tap on mobile focuses the avatar
//   button and announces the same name.

function initialsOf(name = "") {
  const parts = (name || "").trim().split(/\s+/);
  if (!parts.length || !parts[0]) return "•";
  const first = parts[0][0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] || "" : "";
  return (first + last).toUpperCase();
}

export default function TherapistAvatars({ crewIds = [], allCrew = [], size = 28, max = 5, testIdPrefix = "" }) {
  const active = (allCrew || []).filter((c) => c.active !== false);
  const chosen = (crewIds && crewIds.length)
    ? active.filter((c) => crewIds.includes(c.id))
    : active;
  // Dedupe by id, keep original order
  const seen = new Set();
  const list = [];
  for (const c of chosen) {
    if (!c.id || seen.has(c.id)) continue;
    seen.add(c.id);
    list.push(c);
  }
  if (!list.length) return null;

  const visible = list.slice(0, max);
  const extra = list.length - visible.length;
  const dim = `${size}px`;

  return (
    <div
      className="inline-flex items-center"
      role="group"
      aria-label={`Therapists: ${list.map((c) => c.name).join(", ")}`}
      data-testid={`${testIdPrefix}therapist-avatars`}
    >
      <div className="flex -space-x-2">
        {visible.map((c, idx) => (
          <button
            key={c.id}
            type="button"
            tabIndex={0}
            aria-label={`Therapist ${c.name}`}
            title={c.name}
            className="relative rounded-full ring-2 ring-card bg-muted overflow-hidden focus:outline-none focus:ring-accent transition-transform hover:z-10 hover:scale-110"
            style={{ width: dim, height: dim, zIndex: visible.length - idx }}
            data-testid={`${testIdPrefix}therapist-avatar-${c.id}`}
          >
            {c.image_path ? (
              <img
                src={fileUrl(c.image_path)}
                alt={c.name}
                loading="lazy"
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <span
                className="w-full h-full flex items-center justify-center text-[10px] font-medium text-primary/70 bg-gradient-to-br from-muted to-accent/10"
                aria-hidden="true"
              >
                {initialsOf(c.name)}
              </span>
            )}
          </button>
        ))}
        {extra > 0 && (
          <span
            className="rounded-full ring-2 ring-card bg-muted text-[10px] font-medium text-primary/70 flex items-center justify-center"
            style={{ width: dim, height: dim }}
            title={list.slice(max).map((c) => c.name).join(", ")}
            aria-label={`+${extra} more therapists`}
            data-testid={`${testIdPrefix}therapist-avatar-more`}
          >
            +{extra}
          </span>
        )}
      </div>
    </div>
  );
}
